import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { FormInput } from "../../../forms/FormInput";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { WriteReviewFormMode } from "../constants";
import { IWriteReviewFormState } from "../models";
import {
    reviewSetCustomerInformationEmail,
    reviewSetCustomerInformationLocation,
    reviewSetCustomerInformationName,
} from "../reducers";
import { getCustomerInformationState } from "../selectors";

type IProps = Immutable<{
    email?: string;
}>;

const isOpenForm = (formState: IWriteReviewFormState): boolean =>
    formState.mode === WriteReviewFormMode.FORM_OPEN;

const getNameErrors = (forms: {
    [formUUID: string]: IWriteReviewFormState;
}): readonly string[] => {
    const errForm = Object.entries(forms).find(([_i, formState]) => {
        return (
            isOpenForm(formState) &&
            formState.errors.name &&
            formState.errors.name.length
        );
    });
    return errForm ? errForm[1].errors.name : [];
};

const getLocationErrors = (forms: {
    [formUUID: string]: IWriteReviewFormState;
}): readonly string[] => {
    const errForm = Object.entries(forms).find(([_i, formState]) => {
        return (
            isOpenForm(formState) &&
            formState.errors.location &&
            formState.errors.location.length
        );
    });
    return errForm ? errForm[1].errors.location : [];
};

const getEmailErrors = (forms: {
    [formUUID: string]: IWriteReviewFormState;
}): readonly string[] => {
    const errForm = Object.entries(forms).find(([_i, formState]) => {
        return (
            isOpenForm(formState) &&
            formState.errors.email_collection &&
            formState.errors.email_collection.length
        );
    });
    return errForm ? errForm[1].errors.email_collection : [];
};

export const MultiProductWriteReviewFormGlobalInputSection = (
    _props: IProps,
): React.ReactNode => {
    const dispatch = useAppDispatch();
    const userInputFormState = useAppSelector((state) =>
        getCustomerInformationState(state.reviews),
    );
    const forms = useAppSelector((state) => state.reviews.ui.writeReviewForms);

    const handleEmailChange = (e: React.FormEvent<HTMLInputElement>) => {
        dispatch(reviewSetCustomerInformationEmail(e.currentTarget.value));
    };

    const handleNameChange = (e: React.FormEvent<HTMLInputElement>) => {
        dispatch(reviewSetCustomerInformationName(e.currentTarget.value));
    };

    const handleLocationChange = (e: React.FormEvent<HTMLInputElement>) => {
        dispatch(reviewSetCustomerInformationLocation(e.currentTarget.value));
    };

    const nameErrors = getNameErrors(forms);
    const locationErrors = getLocationErrors(forms);
    const emailErrors = getEmailErrors(forms);

    return (
        <div>
            <FormInput
                type="text"
                name="name"
                id="name"
                onChange={handleNameChange}
                required={true}
                label={t`Nickname`}
                labelPlacement={"static"}
                placeholder={t`e.g., Happy Sleeper`}
                value={userInputFormState.name}
                errors={nameErrors}
                showErrorMessages={Boolean(nameErrors.length)}
            />
            <FormInput
                type="text"
                name="location"
                id="location"
                onChange={handleLocationChange}
                required={true}
                label={t`Location`}
                labelPlacement={"static"}
                placeholder={t`e.g., Boston, MA`}
                value={userInputFormState.location}
                errors={locationErrors}
                showErrorMessages={Boolean(locationErrors.length)}
            />
            <FormInput
                type="email"
                name="email"
                id="email"
                onChange={handleEmailChange}
                required={true}
                label={t`Your email address`}
                labelPlacement={"static"}
                placeholder={t`e.g., jdoe@example.com`}
                value={userInputFormState.email_collection}
                errors={emailErrors}
                showErrorMessages={Boolean(emailErrors.length)}
            />
        </div>
    );
};
